import { useMemo, useState } from "react";
import PageHeader from "@/components/feature/PageHeader";
import EmptyState from "@/components/base/EmptyState";
import FilterChips from "@/components/base/FilterChips";
import RecommendCard from "@/components/cards/RecommendCard";
import { recommendList } from "@/mocks/recommend";
import { useSeo } from "@/hooks/useSeo";

export default function RecommendPage() {
  const [genre, setGenre] = useState("all");
  const [year, setYear] = useState("all");

  useSeo({
    title: "相关影视推荐 - 蜜桃成熟时3D",
    description:
      "与蜜桃成熟时3D题材风格相近的影视作品推荐合集，支持按年份与类型筛选，帮助你发现更多值得一看的电影。",
    keywords: "影视推荐,蜜桃成熟时3D,电影推荐,相似电影",
    path: "/recommend",
  });

  const genreOptions = useMemo(() => {
    const set = new Set(recommendList.map((item) => item.genre.split(" / ")[0]));
    return [{ label: "全部类型", value: "all" }, ...Array.from(set).map((g) => ({ label: g, value: g }))];
  }, []);

  const yearOptions = useMemo(() => {
    const set = new Set(recommendList.map((item) => item.year));
    const years = Array.from(set).sort((a, b) => b.localeCompare(a));
    return [{ label: "全部年份", value: "all" }, ...years.map((y) => ({ label: y, value: y }))];
  }, []);

  const filtered = useMemo(
    () =>
      recommendList.filter((item) => {
        const genreOk = genre === "all" || item.genre.split(" / ")[0] === genre;
        const yearOk = year === "all" || item.year === year;
        return genreOk && yearOk;
      }),
    [genre, year],
  );

  return (
    <>
      <PageHeader
        eyebrow="RECOMMEND"
        title="相关影视推荐"
        description="这里汇集了与「蜜桃成熟时3D」题材或风格相近的影视作品，仅作观看参考。你可以按类型与年份筛选，找到更符合口味的选择。"
        crumbs={[{ label: "相关影视推荐" }]}
      >
        <div className="flex flex-col gap-3">
          <FilterChips options={genreOptions} value={genre} onChange={setGenre} label="类型" />
          <FilterChips options={yearOptions} value={year} onChange={setYear} label="年份" />
        </div>
      </PageHeader>

      <section className="bg-background-50 py-10 md:py-14">
        <div className="mx-auto max-w-[1200px] px-4 md:px-6">
          <p className="mb-6 text-sm text-foreground-500">
            共 <span className="font-medium text-primary-700">{filtered.length}</span> 部推荐作品
          </p>

          {filtered.length > 0 ? (
            <div className="grid grid-cols-2 gap-4 md:gap-6 lg:grid-cols-4">
              {filtered.map((item) => (
                <RecommendCard key={item.id} item={item} />
              ))}
            </div>
          ) : (
            <EmptyState
              icon="ri-movie-2-line"
              title="没有符合条件的推荐"
              description="试着放宽类型或年份筛选条件，看看其他作品。"
              actionLabel="重置筛选"
              onAction={() => { setGenre("all"); setYear("all"); }}
            />
          )}

          <p className="mt-10 rounded-lg border border-dashed border-background-300 bg-background-100/60 px-5 py-4 text-center text-xs leading-relaxed text-foreground-500">
            温馨提示：本页推荐内容为虚构演示样例，仅用于展示页面结构，不代表真实影视作品。
          </p>
        </div>
      </section>
    </>
  );
}